import React, { useEffect, useState } from 'react';
import './styles.scss';
import { getHorseImage } from './horseImages';

// Two distinct screens sharing one shell/header/footer: the shop (no horse
// owned yet - grid of buyable breeds with portraits) and the management
// panel (owned horse - portrait, hunger gauge, Nourrir/Appeler/Ranger/
// Vendre). Which one renders is decided purely by `current` (null vs. not),
// same server-is-the-only-source-of-truth shape as before - see
// horseSystem.ts's openStableCatalog for the payload this consumes.
// Portraits are Keizaal's own horse asset set (Z:\Antoine\UI\assets\horses),
// reused with their explicit go-ahead - see horseImages.ts.

interface BreedChoice {
  breedId: string;
  name: string;
  price: number;
  affordable: boolean;
}

interface CurrentHorse {
  breedId: string;
  name: string;
  hunger: number;
  hungerMax: number;
  state: 'stabled' | 'summoned';
  canSummon: boolean;
  canFeed: boolean;
  feedRestoreAmount: number;
}

interface ShowEventDetail {
  breeds: BreedChoice[];
  current: CurrentHorse | null;
  sellRefund: number | null;
}

const send = (payload: Record<string, unknown>): void => {
  if ((window as any).skyrimPlatform?.sendMessage) {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify({ source: 'horse-stable', ...payload }));
  }
};

// Same click-grace guard as CraftMenu/ProfessionsMenu/TargetMenu - browser
// focus flaps while the overlay is toggled on, so ignore any click for a
// brief window after opening.
const CLICK_GRACE_MS = 250;

const HorseStable: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [breeds, setBreeds] = useState<BreedChoice[]>([]);
  const [current, setCurrent] = useState<CurrentHorse | null>(null);
  const [sellRefund, setSellRefund] = useState<number | null>(null);
  const [confirmingSell, setConfirmingSell] = useState(false);
  const openedAtRef = React.useRef(0);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as ShowEventDetail;
      setBreeds(detail.breeds ?? []);
      setCurrent(detail.current ?? null);
      setSellRefund(detail.sellRefund ?? null);
      // Don't reset confirmingSell here - a "feed" round-trip re-fires this
      // same event to refresh hunger in place (see horseStableService.ts's
      // onBrowserMessage), and closing the sell-confirm prompt out from
      // under the player mid-flow would be a worse surprise than just
      // leaving it as-is; it only ever opens via askSell's own click anyway.
      setVisible(true);
      openedAtRef.current = Date.now();
    };
    window.addEventListener('skymp5-client:showHorseStable', handler);
    return () => window.removeEventListener('skymp5-client:showHorseStable', handler);
  }, []);

  const close = () => {
    setVisible(false);
    setConfirmingSell(false);
    send({ action: 'close' });
  };

  const guardClick = (e: React.MouseEvent): boolean => {
    if (e.detail === 0) return false;
    return Date.now() - openedAtRef.current >= CLICK_GRACE_MS;
  };

  const buy = (e: React.MouseEvent, breed: BreedChoice) => {
    if (!guardClick(e) || !breed.affordable || current !== null) return;
    setVisible(false);
    send({ action: 'buy', breedId: breed.breedId });
  };

  const askSell = (e: React.MouseEvent) => {
    if (!guardClick(e)) return;
    setConfirmingSell(true);
  };

  const confirmSell = (e: React.MouseEvent) => {
    if (!guardClick(e)) return;
    setVisible(false);
    send({ action: 'sell' });
  };

  const cancelSell = (e: React.MouseEvent) => {
    if (!guardClick(e)) return;
    setConfirmingSell(false);
  };

  const toggleCall = (e: React.MouseEvent) => {
    if (!guardClick(e) || !current) return;
    setVisible(false);
    send({ action: current.state === 'summoned' ? 'store' : 'summon' });
  };

  const feed = (e: React.MouseEvent) => {
    if (!guardClick(e) || !current || !current.canFeed) return;
    // Deliberately stays open - horseStableService.ts skips the close for
    // "feed" and the server pushes a fresh horseStableOpen right after.
    send({ action: 'feed' });
  };

  if (!visible) return null;

  const portrait = current ? getHorseImage(current.breedId) : null;
  const hungerPct = current ? Math.max(0, Math.min(100, (current.hunger / Math.max(1, current.hungerMax)) * 100)) : 0;

  return (
    <div className="horse-stable-overlay">
      <div className="horse-stable-shell">
        <header className="horse-stable-header">
          <h1 className="horse-stable-header__title">Écurie</h1>
        </header>

        <div className="horse-stable-body">
          {current ? (
            <div className="horse-manage">
              <div className="horse-manage__portrait-frame">
                {portrait && <img className="horse-manage__portrait" src={portrait} alt={current.name} />}
              </div>

              <div className="horse-manage__info">
                <div className="horse-manage__name-row">
                  <span className="horse-manage__name">{current.name}</span>
                  <span className={`horse-manage__state horse-manage__state--${current.state}`}>
                    {current.state === 'summoned' ? 'Appelé' : "À l'écurie"}
                  </span>
                </div>

                <div className="horse-manage__hunger">
                  <div className="horse-manage__hunger-bar">
                    <div
                      className={[
                        'horse-manage__hunger-fill',
                        hungerPct <= 30 ? 'horse-manage__hunger-fill--low' : '',
                      ].filter(Boolean).join(' ')}
                      style={{ width: `${hungerPct}%` }}
                    />
                  </div>
                  <span className="horse-manage__hunger-label">
                    Faim {current.hunger}/{current.hungerMax}
                  </span>
                </div>

                {!current.canSummon && current.state === 'stabled' && (
                  <p className="horse-manage__warning">Nourris ton cheval avant de pouvoir l'appeler.</p>
                )}

                <div className="horse-manage__actions">
                  <button
                    type="button"
                    className="horse-manage__action horse-manage__action--feed"
                    disabled={!current.canFeed}
                    onClick={feed}
                  >
                    Nourrir{current.feedRestoreAmount > 0 ? ` (+${current.feedRestoreAmount})` : ''}
                  </button>
                  <button
                    type="button"
                    className="horse-manage__action horse-manage__action--primary"
                    disabled={current.state === 'stabled' && !current.canSummon}
                    onClick={toggleCall}
                  >
                    {current.state === 'summoned' ? 'Ranger' : 'Appeler'}
                  </button>
                </div>
              </div>

              <div className="horse-manage__sell-row">
                {!confirmingSell ? (
                  <button type="button" className="horse-stable-sell__button" onClick={askSell}>
                    Vendre ({sellRefund ?? 0} Septims)
                  </button>
                ) : (
                  <div className="horse-stable-sell-confirm">
                    <span className="horse-stable-sell-confirm__text">
                      Vendre {current.name} pour {sellRefund ?? 0} Septims ? Cette action est définitive.
                    </span>
                    <div className="horse-stable-sell-confirm__actions">
                      <button type="button" className="horse-stable-sell-confirm__confirm" onClick={confirmSell}>
                        Confirmer
                      </button>
                      <button type="button" className="horse-stable-sell-confirm__cancel" onClick={cancelSell}>
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="horse-shop-grid">
              {breeds.map((breed) => {
                const img = getHorseImage(breed.breedId);
                return (
                  <button
                    type="button"
                    key={breed.breedId}
                    className={[
                      'horse-shop-card',
                      breed.affordable ? 'horse-shop-card--buyable' : '',
                    ].filter(Boolean).join(' ')}
                    disabled={!breed.affordable}
                    onClick={(e) => buy(e, breed)}
                  >
                    <div className="horse-shop-card__portrait-frame">
                      {img && <img className="horse-shop-card__portrait" src={img} alt={breed.name} />}
                    </div>
                    <span className="horse-shop-card__name">{breed.name}</span>
                    <span className="horse-shop-card__price">{breed.price} Septims</span>
                    {!breed.affordable && <span className="horse-shop-card__lock">🔒</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <footer className="horse-stable-footer">
          <button type="button" className="horse-stable-close-btn" onClick={close}>Fermer</button>
        </footer>
      </div>
    </div>
  );
};

export default HorseStable;
